/* eslint-disable arrow-body-style */
import React, { useMemo, useState, useEffect } from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import Shortcuts from '@intelligence/components/GenreSelect/components/Shortcuts';
import Checkboxbars from '@intelligence/components/GenreSelect/components/Checkboxbars';
import useScrollActiveIntoView from '@intelligence/components/GenreSelect/_hooks/useScrollActiveIntoView';
import { Relation, ShortcutActionType, ShortcutMatchType } from '@intelligence/components/GenreSelect/enums';

import type {
  IBaseProps,
  IShortcut,
  IGenreCheckboxOptionWithChildren,
} from '@intelligence/components/GenreSelect/types';

import './index.scss';

/**
 * 单层级联品类列表：复用 Checkboxbars，并在挂载/激活项变化时把激活项滚动到可视区。
 * 抽成内部组件是为了在循环渲染各层级时合法地调用 hook（hook 不能在循环里调用）。
 */
interface ICascadeLevelCheckboxbarsProps {
  level: number;
  items: IGenreCheckboxOptionWithChildren[];
  keywords?: any;
  activeGenreValue?: string;
  hasChildren: (genre: IGenreCheckboxOptionWithChildren) => boolean;
  onCheckboxChange: (level: number, item: IGenreCheckboxOptionWithChildren, checked: boolean) => void;
  onLevelItemClick: (level: number, index: number) => void;
}

const CascadeLevelCheckboxbars: React.FC<ICascadeLevelCheckboxbarsProps> = (props) => {
  const { level, items, keywords, activeGenreValue, hasChildren, onCheckboxChange, onLevelItemClick } = props;

  // 激活项变化或品类数据变化时，把激活项滚动到可视区（仅在不可见时滚动）
  const scrollRef = useScrollActiveIntoView([activeGenreValue, items]);

  return (
    <Checkboxbars
      level={level}
      containerRef={scrollRef}
      genres={items}
      keywords={keywords}
      showRightArrow={(genre: IGenreCheckboxOptionWithChildren) => hasChildren(genre)}
      active={(genreValue) => genreValue === activeGenreValue}
      indeterminate={(genre) => !!genre?.indeterminate}
      onChange={(value, checked) => {
        const item = items?.find((i) => i?.value === value);
        if (item) {
          onCheckboxChange(level, item, checked);
        }
      }}
      onClick={(index, genreValue) => {
        const item = items?.find((i) => i?.value === genreValue);
        if (item) {
          // 如果没有子级数据,直接勾选该品类
          if (!hasChildren(item)) {
            onCheckboxChange(level, item, !item?.checked);
          } else {
            // 有子级数据时,展开下一级
            onLevelItemClick(level, index);
          }
        }
      }}
    />
  );
};

interface ICascadeCheckboxPanelsProps extends IBaseProps {
  currentIndexResetFlag?: any;
  shortcuts?: IShortcut[];
  allGenres: IGenreCheckboxOptionWithChildren[];
  genres: IGenreCheckboxOptionWithChildren[];
  activeIndices?: number[];
  onActiveIndicesChange?: (indices: number[]) => void;
  onSubGenreCheckedChange?: (genre: string, relation: Relation, subGenre: string, checked: boolean) => void;
  onSubGenreAllCheckedChange?: (genre: string, relation: Relation, checked: boolean) => void;
  onGenreCheckedChange?: (genre: string, relation: Relation, checked: boolean) => void;
  onGenreAllCheckedChange?: (checked: boolean) => void;
  onShortcutClick?: (shortcut: IShortcut) => void;
  /**
   * App Magic 级联品类勾选回调
   * @param path 从根节点到目标节点的路径（value 数组）
   * @param checked 是否勾选
   */
  onCascadeCheckedChange?: (path: string[], checked: boolean) => void;
  /**
   * App Magic 级联品类全选/取消全选回调
   * @param path 从根节点到父节点的路径（value 数组），空数组表示全选所有
   * @param checked 是否全选
   */
  onCascadeAllCheckedChange?: (path: string[], checked: boolean) => void;
}

/**
 * 计算品类树的最大层级深度
 * @param genres 品类数组
 * @param maxDepthLimit 最大深度限制，防止循环引用
 * @returns 最大层级深度
 */
const calculateMaxDepth = (genres: IGenreCheckboxOptionWithChildren[], maxDepthLimit = 10): number => {
  if (!Array.isArray(genres) || !genres.length) return 0;

  let maxDepth = 1;

  const traverse = (items: IGenreCheckboxOptionWithChildren[], currentDepth: number) => {
    if (currentDepth > maxDepthLimit) {
      console.error('[calculateMaxDepth] 递归深度超限,可能存在循环引用, depth:', currentDepth);
      return;
    }

    items.forEach((item) => {
      if (item?.children && Array.isArray(item.children) && item.children.length > 0) {
        const depth = currentDepth + 1;
        if (depth > maxDepth) {
          maxDepth = depth;
        }
        traverse(item.children, depth);
      }
    });
  };

  traverse(genres, 1);
  return maxDepth;
};

/**
 * 递归检查指定路径的所有节点是否都已勾选
 * @param genres 品类数组
 * @param path 路径数组
 * @param currentIndex 当前路径索引
 * @returns 该路径是否全部勾选
 */
const isPathFullyChecked = (genres: IGenreCheckboxOptionWithChildren[], path: string[], currentIndex = 0): boolean => {
  if (currentIndex >= path.length) return true;

  const targetValue = path[currentIndex];
  const matchedItem = genres.find((item) => item?.value === targetValue);

  if (!matchedItem) return false;

  // 检查当前节点及其所有子节点是否都勾选
  const isCurrentItemFullyChecked = (item: IGenreCheckboxOptionWithChildren): boolean => {
    if (!item?.checked) return false;

    const children = item?.children as IGenreCheckboxOptionWithChildren[];
    if (!children || children.length === 0) return true;

    return children.every((child) => isCurrentItemFullyChecked(child));
  };

  // 如果是路径的最后一级，检查该节点及其所有子节点是否都勾选
  if (currentIndex === path.length - 1) {
    return isCurrentItemFullyChecked(matchedItem);
  }

  // 继续检查下一级
  const children = matchedItem?.children as IGenreCheckboxOptionWithChildren[];
  if (!children || children.length === 0) return false;

  return isPathFullyChecked(children, path, currentIndex + 1);
};

/**
 * 递归查找所有匹配快捷品类的路径
 * @param genres 品类数组
 * @param shortcutValue 快捷品类值
 * @param matchType 匹配模式
 * @param currentPath 当前路径
 * @returns 所有匹配的路径数组
 */
const findMatchingPaths = (
  genres: IGenreCheckboxOptionWithChildren[],
  shortcutValue: string,
  matchType: ShortcutMatchType,
  currentPath: string[] = [],
): string[][] => {
  const matchingPaths: string[][] = [];

  genres.forEach((item) => {
    if (!item?.value) return;

    const itemPath = [...currentPath, item.value];
    const isMatch =
      matchType === ShortcutMatchType.Exact ? item.value === shortcutValue : item.value.includes(shortcutValue);

    if (isMatch) {
      matchingPaths.push(itemPath);
    }

    const children = item?.children as IGenreCheckboxOptionWithChildren[];
    if (children && children.length > 0) {
      const childPaths = findMatchingPaths(children, shortcutValue, matchType, itemPath);
      matchingPaths.push(...childPaths);
    }
  });

  return matchingPaths;
};

/**
 * 递归统计所有已勾选的路径数量（用于 Replace 模式判断）
 * @param genres 品类数组
 * @returns 已勾选的路径数量
 */
const countAllCheckedPaths = (genres: IGenreCheckboxOptionWithChildren[]): number => {
  if (!Array.isArray(genres)) return 0;

  let count = 0;

  for (const item of genres) {
    // 如果当前节点已勾选且没有子节点，或者当前节点已勾选且所有子节点都已勾选，则计数+1
    if (item?.checked) {
      const children = item?.children as IGenreCheckboxOptionWithChildren[];
      if (!children || children.length === 0) {
        count += 1;
      } else {
        // 递归检查子节点
        const allChildrenChecked = children.every((child) => {
          const grandChildren = child?.children as IGenreCheckboxOptionWithChildren[];
          if (!grandChildren || grandChildren.length === 0) {
            return !!child?.checked;
          }
          // 递归检查更深的层级
          const checkDeep = (nodes: IGenreCheckboxOptionWithChildren[]): boolean => {
            return nodes.every((node) => {
              if (!node?.checked) return false;
              const nodeChildren = node?.children as IGenreCheckboxOptionWithChildren[];
              if (!nodeChildren || nodeChildren.length === 0) return true;
              return checkDeep(nodeChildren);
            });
          };
          return checkDeep([child]);
        });
        if (allChildrenChecked) {
          count += 1;
        }
      }
    }

    // 递归检查子节点
    const children = item?.children as IGenreCheckboxOptionWithChildren[];
    if (children && children.length > 0) {
      count += countAllCheckedPaths(children);
    }
  }

  return count;
};

/**
 * 判断级联快捷勾选按钮是否禁用
 * Compose模式：所有匹配路径都已勾选时禁用
 * Replace模式：只有当匹配的路径都已勾选，且没有其他已勾选的路径时，才判定为高亮
 * @param shortcutValue 快捷品类值
 * @param matchType 匹配模式
 * @param type 快捷按钮勾选模式
 * @param genres 当前品类集合
 * @returns 快捷按钮是否禁用
 */
const getCascadeShortcutDisabled = (
  shortcutValue: string,
  matchType: ShortcutMatchType,
  type: ShortcutActionType,
  genres?: IGenreCheckboxOptionWithChildren[],
): boolean => {
  if (!Array.isArray(genres) || !genres.length) return false;
  if (!shortcutValue) return false;

  const matchingPaths = findMatchingPaths(genres, shortcutValue, matchType);

  if (matchingPaths.length === 0) return false;

  // 统计匹配的路径中已勾选的数量
  const matchedAndCheckedLength = matchingPaths.filter((path) => isPathFullyChecked(genres, path)).length;

  // Replace 模式：只有当匹配的路径都已勾选，且没有其他已勾选的路径时，才高亮
  if (type === ShortcutActionType.Replace) {
    const totalCheckedLength = countAllCheckedPaths(genres);
    return matchingPaths.length === matchedAndCheckedLength && matchedAndCheckedLength === totalCheckedLength;
  }

  // Compose 模式：所有匹配路径都已勾选时，高亮
  return matchingPaths.length === matchedAndCheckedLength;
};

/**
 * 给级联品类快捷勾选按钮集合追加disabled状态
 * @param shortcuts 品类快捷勾选按钮集合
 * @param genres 当前品类集合
 * @returns 品类快捷勾选按钮集合（带disabled状态）
 */
const addCascadeShortcutsDisabledState = (
  shortcuts?: IShortcut[],
  genres?: IGenreCheckboxOptionWithChildren[],
): IShortcut[] => {
  if (!Array.isArray(shortcuts) || !shortcuts.length) return [];
  if (!Array.isArray(genres) || !genres.length) return shortcuts;

  return shortcuts.map((shortcut) => {
    const matchType = shortcut?.matchType ?? ShortcutMatchType.Exact;
    const shortcutDisabled = getCascadeShortcutDisabled(shortcut?.value, matchType, shortcut?.type, genres);
    return { ...shortcut, disabled: shortcutDisabled };
  });
};

/**
 * 根据选中索引构建每一层应该显示的数据
 * @param genres 品类数据
 * @param depth 最大层级深度
 * @param selectedIndices 每一层选中的索引
 */
const buildLevelDataByIndices = (
  genres: IGenreCheckboxOptionWithChildren[],
  depth: number,
  selectedIndices: number[],
): IGenreCheckboxOptionWithChildren[][] => {
  const paths: IGenreCheckboxOptionWithChildren[][] = [];

  for (let i = 0; i < depth; i++) {
    paths.push([]);
  }

  if (!genres || genres.length === 0) return paths;

  // 第一层：所有主品类
  paths[0] = genres;

  // 根据 selectedIndices 递归构建每一层的数据
  let currentLevel = genres;
  let currentDepth = 1;

  while (currentDepth < depth && currentLevel?.length > 0) {
    const selectedIndex = selectedIndices[currentDepth - 1] ?? 0;
    const selectedItem = currentLevel[selectedIndex] || currentLevel[0];

    if (selectedItem && selectedItem.children?.length > 0) {
      const children = selectedItem.children as IGenreCheckboxOptionWithChildren[];
      paths[currentDepth] = children;
      currentLevel = children;
      // eslint-disable-next-line no-plusplus
      currentDepth++;
    } else {
      break;
    }
  }

  return paths;
};

const getValidActiveIndices = (indices?: number[]): number[] => {
  if (!Array.isArray(indices) || indices.length === 0) {
    return [0];
  }

  return indices;
};

const CascadeCheckboxPanels: React.FC<ICascadeCheckboxPanelsProps> = (props) => {
  const {
    style,
    className,
    currentIndexResetFlag,
    shortcuts: shortcutsProps,
    allGenres,
    genres,
    activeIndices,
    onActiveIndicesChange,
    onSubGenreCheckedChange,
    onSubGenreAllCheckedChange,
    onGenreCheckedChange,
    onGenreAllCheckedChange,
    onShortcutClick,
    onCascadeCheckedChange,
    onCascadeAllCheckedChange,
  } = props;

  const { formatMessage } = useIntl();

  // 计算最大层级深度
  const maxDepth = useMemo(() => calculateMaxDepth(allGenres), [allGenres]);

  // 当前每一层选中的索引
  const [selectedIndices, setSelectedIndices] = useState<number[]>(getValidActiveIndices(activeIndices));

  const syncSelectedIndices = (indices: number[]) => {
    setSelectedIndices(indices);
    onActiveIndicesChange?.(indices);
  };

  // 根据选中索引计算每一层应该显示的数据
  const levelData = useMemo(() => {
    return buildLevelDataByIndices(genres, maxDepth, selectedIndices);
  }, [genres, maxDepth, selectedIndices]);

  // 重置选中索引
  useEffect(() => {
    if (!currentIndexResetFlag) return;
    syncSelectedIndices([0]);
  }, [currentIndexResetFlag]);

  useEffect(() => {
    if (!Array.isArray(activeIndices)) return;
    setSelectedIndices(getValidActiveIndices(activeIndices));
  }, [activeIndices]);

  // 处理某一层级的项目被点击
  const handleLevelItemClick = (level: number, index: number) => {
    const newIndices = [...selectedIndices];
    newIndices[level] = index;
    // 清除后续层级的选中索引
    newIndices.splice(level + 1);
    syncSelectedIndices(newIndices);
  };

  // 构建从根节点到指定层级的路径（value 数组）
  const buildValuePath = (level: number, targetValue?: string): string[] => {
    const path: string[] = [];
    let currentData = genres;

    for (let i = 0; i < level; i++) {
      const index = selectedIndices[i] || 0;
      const item = currentData[index] || currentData[0];
      if (item) {
        path.push(item.value);
        currentData = (item.children as IGenreCheckboxOptionWithChildren[]) || [];
      }
    }

    // 如果有目标值，添加到路径末尾
    if (targetValue) {
      path.push(targetValue);
    }

    return path;
  };

  // 构建父级路径（返回完整的品类对象）
  const buildParentPath = (level: number): IGenreCheckboxOptionWithChildren[] => {
    const path: IGenreCheckboxOptionWithChildren[] = [];
    let currentData = genres;

    for (let i = 0; i < level; i++) {
      const index = selectedIndices[i] || 0;
      const item = currentData[index] || currentData[0];
      if (item) {
        path.push(item);
        currentData = (item.children as IGenreCheckboxOptionWithChildren[]) || [];
      }
    }

    return path;
  };

  // 处理复选框变化
  const handleCheckboxChange = (level: number, item: IGenreCheckboxOptionWithChildren, checked: boolean) => {
    // 优先使用级联回调（App Magic 专用）
    if (onCascadeCheckedChange) {
      const path = buildValuePath(level, item.value);
      onCascadeCheckedChange(path, checked);
      return;
    }

    // 兼容旧的回调方式
    if (level === 0) {
      // 第一层：主品类
      onGenreCheckedChange?.(item.value, Relation.Or, checked);
    } else {
      // 其他层级：需要找到父级路径
      const parentPath = buildParentPath(level);
      if (parentPath.length > 0) {
        const parentGenre = parentPath[0];
        onSubGenreCheckedChange?.(parentGenre.value, Relation.Or, item.value, checked);
      }
    }
  };

  // 计算批量操作状态
  const getBatchOperation = (items: IGenreCheckboxOptionWithChildren[]): number => {
    if (!Array.isArray(items) || !items.length) return -1;
    return !items.some((item) => item?.checked || item?.indeterminate) ? 1 : 0;
  };

  // 处理批量全选/取消全选
  const handleBatchOperation = (level: number, allChecked: boolean) => {
    // 优先使用级联回调（App Magic 专用）
    if (onCascadeAllCheckedChange) {
      const path = buildValuePath(level);
      onCascadeAllCheckedChange(path, allChecked);
      return;
    }

    // 兼容旧的回调方式
    if (level === 0) {
      onGenreAllCheckedChange?.(allChecked);
    } else {
      const parentPath = buildParentPath(level);
      if (parentPath.length > 0) {
        const parentGenre = parentPath[parentPath.length - 1];
        onSubGenreAllCheckedChange?.(parentGenre.value, Relation.Or, allChecked);
      }
    }
  };

  // 渲染快捷按钮
  const renderShortcuts = () => {
    if (!shortcutsProps || shortcutsProps.length === 0) return null;

    // 根据当前品类勾选状态动态计算快捷按钮的disabled状态
    const shortcuts = addCascadeShortcutsDisabledState(shortcutsProps, allGenres);

    return (
      <div className="cascade-shortcuts-container">
        <Shortcuts shortcuts={shortcuts} onClick={onShortcutClick} />
      </div>
    );
  };

  // 获取层级标题的国际化 key
  const getLevelTitleKey = (level: number): string => {
    const titleKeys = [
      'dbi_super_genre',
      'dbi_level_1_genre',
      'dbi_level_2_genre',
      'dbi_level_3_genre',
      'dbi_level_4_genre',
    ];
    return titleKeys[level] || 'dbi_sub_genre';
  };

  // 获取当前层级激活品类的 value，供滚动定位依赖使用
  const getActiveGenreValue = (level: number): string | undefined => {
    const items = levelData[level] || [];
    const selectedIndex = selectedIndices[level] ?? 0;
    const selectedItem = items[selectedIndex] || items[0];
    return selectedItem?.value;
  };

  // 判断某个品类是否有子级
  const hasChildren = (item: IGenreCheckboxOptionWithChildren): boolean => {
    return !!(item?.children && item.children.length > 0);
  };

  // 渲染单个层级面板
  const renderLevelPanel = (level: number, levelClassName?: string) => {
    const items = levelData[level] || [];
    const title = formatMessage({ id: getLevelTitleKey(level) });

    // 当前层级无数据时显示无数据提示
    if (items.length === 0) {
      return (
        <div key={level} className={classnames('cascade-level-panel', levelClassName)}>
          <div className="cascade-level-header">
            <span className="cascade-level-title">{title}</span>
          </div>
          <div className="cascade-level-content cascade-level-no-data">
            <span className="genre-no-data">{formatMessage({ id: 'dbi_no_results_found' })}</span>
          </div>
        </div>
      );
    }

    const batchOperation = getBatchOperation(items);

    return (
      <div key={level} className={classnames('cascade-level-panel', levelClassName)}>
        {level === 0 && renderShortcuts()}
        <div className="cascade-level-header">
          <span className="cascade-level-title">{title}</span>
          {batchOperation !== -1 && (
            <span className="cascade-batch-operation" onClick={() => handleBatchOperation(level, batchOperation === 1)}>
              {batchOperation === 1 ? formatMessage({ id: 'dbi_select_all' }) : formatMessage({ id: 'dbi_clear_all' })}
            </span>
          )}
        </div>
        <div className="cascade-level-content">
          <CascadeLevelCheckboxbars
            level={level}
            items={items}
            keywords={currentIndexResetFlag}
            activeGenreValue={getActiveGenreValue(level)}
            hasChildren={hasChildren}
            onCheckboxChange={handleCheckboxChange}
            onLevelItemClick={handleLevelItemClick}
          />
        </div>
      </div>
    );
  };

  // 渲染所有层级面板
  // 遇到第一个无数据的层级时，只展示该层无数据面板，不再继续渲染后续层级，
  // 避免连续展示多个层级的无品类数据勾选子面板；
  // 最后一层（第 5 层）无数据时不展示该层无数据面板，只有有数据时才展示
  // divider 采用前置方式（起始层不加），自然保证层间有 divider、末层后无 divider
  const renderPanels = (startLevel = 0) => {
    const panels = [];
    for (let i = startLevel; i < maxDepth; i++) {
      const items = levelData[i] || [];
      const isLastLevel = i === maxDepth - 1;

      // 最后一层无数据时跳过该层，不展示无数据面板
      if (isLastLevel && items.length === 0) {
        break;
      }

      // 前置分隔线（起始层不加）
      if (i > startLevel) {
        panels.push(<div key={`divider-${i}`} className="cascade-divider" />);
      }

      panels.push(renderLevelPanel(i));

      // 当前层级无数据时，只展示这一层无数据面板后停止渲染
      if (items.length === 0) {
        break;
      }
    }
    return panels;
  };

  const renderPanelsContainer = () => {
    if (maxDepth !== 5) {
      return <div className="cascade-panels-container">{renderPanels()}</div>;
    }

    const hasFirstLevelItems = (levelData[0] || []).length > 0;

    return (
      <div className="cascade-panels-container">
        {renderLevelPanel(0, 'cascade-fixed-level-panel')}
        {hasFirstLevelItems && <div className="cascade-scrollable-panels">{renderPanels(1)}</div>}
      </div>
    );
  };

  // 判断是否无数据
  const isNoData = !Array.isArray(genres) || !genres.length;

  const renderContent = () => {
    if (isNoData) {
      return (
        <div className="cascade-no-data">
          <span className="genre-no-data">{formatMessage({ id: 'dbi_no_results_found' })}</span>
        </div>
      );
    }

    return renderPanelsContainer();
  };

  return (
    <div style={style} className={classnames('cascade-checkbox-panels', className)} data-depth={maxDepth}>
      {renderContent()}
    </div>
  );
};

export default CascadeCheckboxPanels;
